/**
 * Interview Components - Shared Constants and Utilities
 * Used across all interview-related components
 */

// WPM Configuration
export const WPM_CONFIG = {
  minOptimal: 120,
  maxOptimal: 150,
  tooSlow: 100,
  tooFast: 180
};

// Interview Modes
export const INTERVIEW_MODES = {
  standard: {
    name: "Standard",
    icon: "MessageSquare",
    description: "Traditional interview format"
  },
  panel: {
    name: "Panel",
    icon: "Users",
    description: "Multiple interviewers"
  },
  stress: {
    name: "Stress",
    icon: "Zap",
    description: "High-pressure scenarios"
  },
  technical: {
    name: "Technical",
    icon: "Code",
    description: "Coding challenges"
  },
  salary: {
    name: "Salary",
    icon: "DollarSign",
    description: "Negotiation practice"
  }
};

// Experience Levels
export const EXPERIENCE_LEVELS = [
  { value: "entry", label: "Entry Level (0-2 years)" },
  { value: "mid", label: "Mid Level (3-5 years)" },
  { value: "senior", label: "Senior (6-10 years)" },
  { value: "lead", label: "Lead/Manager (10+ years)" }
];

// Company Cultures
export const COMPANY_CULTURES = [
  { value: "corporate", label: "Corporate" },
  { value: "startup", label: "Startup" },
  { value: "remote", label: "Remote-First" },
  { value: "consulting", label: "Consulting" }
];

// Industries
export const INDUSTRIES = [
  { value: "technology", label: "Technology" },
  { value: "finance", label: "Finance/Banking" },
  { value: "healthcare", label: "Healthcare" },
  { value: "consulting", label: "Consulting" },
  { value: "ecommerce", label: "E-Commerce" },
  { value: "manufacturing", label: "Manufacturing" }
];

// Utility Functions
export const getWPMStatus = (wpm) => {
  if (wpm === 0) return { status: "Ready", bgColor: "bg-gray-100", textColor: "text-gray-600" };
  if (wpm < WPM_CONFIG.tooSlow) return { status: "Too Slow", bgColor: "bg-red-50", textColor: "text-red-600" };
  if (wpm < WPM_CONFIG.minOptimal) return { status: "Slow", bgColor: "bg-orange-50", textColor: "text-orange-600" };
  if (wpm <= WPM_CONFIG.maxOptimal) return { status: "Optimal", bgColor: "bg-green-50", textColor: "text-green-600" };
  if (wpm <= WPM_CONFIG.tooFast) return { status: "Fast", bgColor: "bg-orange-50", textColor: "text-orange-600" };
  return { status: "Too Fast", bgColor: "bg-red-50", textColor: "text-red-600" };
};

export const getWPMMeterValue = (wpm) => {
  // Map WPM to percentage (0-100) for meter display
  // 0 WPM = 0%, 200+ WPM = 100%
  return Math.min((wpm / 200) * 100, 100);
};

export const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

export const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export const getScoreColor = (score) => {
  if (score >= 80) return "text-green-600";
  if (score >= 60) return "text-blue-600";
  if (score >= 40) return "text-yellow-600";
  return "text-red-600";
};

export const getScoreBgColor = (score) => {
  if (score >= 80) return "bg-green-100";
  if (score >= 60) return "bg-blue-100";
  if (score >= 40) return "bg-yellow-100";
  return "bg-red-100";
};
